import type { CartNotification } from '@libs/common-types';
import { isCartNotificationTreatPointsRemoved } from '@libs/common-types';
import { CartNotificationAction } from '@libs/core';

export const createTreatPointsRemovedCartNotification = (errorCode?: string): CartNotification =>
    ({
        notificationType: 'TreatPointsRemoved',
        action: CartNotificationAction.TREAT_POINTS_REMOVED,
        ...(errorCode ? { errorCode } : {}),
    }) satisfies CartNotification;

export const appendTreatPointsRemovedCartNotification = (
    prev: CartNotification[] | undefined | null,
    errorCode?: string
): CartNotification[] => [
    ...(prev ?? []).filter(notification => !isCartNotificationTreatPointsRemoved(notification)),
    createTreatPointsRemovedCartNotification(errorCode),
];

export type TreatPointsRemovedRecoveryRefetchFailureMode = 'throw' | 'return-failed';

export type RunTreatPointsRemovedRecoveryOptions = {
    setNotifications: (updater: (prev: CartNotification[] | undefined) => CartNotification[]) => void;
    refetchCart: (options?: { mergePersistedNotifications?: boolean }) => Promise<unknown>;
    clearErrors: () => void;
    clearStockErrors: () => void;
    whenRefetchFails?: TreatPointsRemovedRecoveryRefetchFailureMode;
};

export const runTreatPointsRemovedRecovery = async (
    options: RunTreatPointsRemovedRecoveryOptions
): Promise<{ refetchOk: boolean }> => {
    const { setNotifications, refetchCart, clearErrors, clearStockErrors, whenRefetchFails = 'throw' } = options;

    setNotifications(prev => appendTreatPointsRemovedCartNotification(prev));

    let refetchOk = true;
    try {
        await refetchCart({ mergePersistedNotifications: true });
    } catch (error: unknown) {
        refetchOk = false;
        if (whenRefetchFails === 'return-failed') {
            return { refetchOk };
        }
        throw error;
    }

    clearStockErrors();
    clearErrors();
    return { refetchOk };
};
